import { useEffect, useRef, useState, type ReactNode } from "react";
import { Check, Copy, Reply } from "lucide-react";
import { Button } from "../atoms/Button/index.js";
import { Tooltip } from "../molecules/Tooltip/index.js";
import {
  MessageFeedbackControls,
  RESPONSE_ACTIONS_ROW,
} from "./MessageFeedbackControls";
import type {
  PlayerAIChatMessage,
  PlayerAIMessageFeedback,
  PlayerAIMessageFeedbackRequest,
} from "./playerai.types";

/** How long "Copied" stays before the control returns to "Copy". */
const COPIED_CONFIRMATION_MS = 2000;

/**
 * Task 9 — the ONE action row under a Player AI response, in the agreed order:
 * Reply · Copy · thumbs up · thumbs down. The thumbs keep their own behaviour
 * (MessageFeedbackControls); this only puts Reply and Copy in front of them,
 * inside the same row, so the row reveals and hides as one.
 */
export function AssistantMessageActions({
  message,
  onReply,
  onMessageFeedback,
}: {
  message: PlayerAIChatMessage;
  onReply?: ((message: PlayerAIChatMessage) => void) | undefined;
  onMessageFeedback?:
    | ((
        feedback: PlayerAIMessageFeedbackRequest,
      ) => Promise<PlayerAIMessageFeedback>)
    | undefined;
}) {
  const text = message.content ?? "";
  // A persisted, completed response only: a streamed draft, a document-wait
  // prompt or a failure card is not an answer anyone should reply to or copy.
  if (!text.trim() || !message.feedbackEligible) return null;
  const leading: ReactNode = (
    <>
      {message.canReply && onReply ? (
        <Tooltip content="Reply" describeChild={false}>
          <Button
            variant="ghost"
            size="xs"
            iconOnly
            aria-label="Reply"
            onClick={() => onReply(message)}
          >
            <Reply className="h-3.5 w-3.5" aria-hidden />
          </Button>
        </Tooltip>
      ) : null}
      <ResponseCopyButton text={text} />
    </>
  );
  if (message.feedbackEligible && message.conversationId && onMessageFeedback) {
    return (
      <MessageFeedbackControls
        message={message}
        conversationId={message.conversationId}
        onSubmit={onMessageFeedback}
        leading={leading}
      />
    );
  }
  return (
    <div
      className={`${RESPONSE_ACTIONS_ROW} opacity-0`}
      role="group"
      aria-label="Response actions"
    >
      {leading}
    </div>
  );
}

/**
 * Copies the response's own text — never its trigger preview, its "+N", the
 * author label or these controls — and confirms only once the clipboard
 * write has actually resolved. A refused write shows nothing rather than a
 * confirmation that did not happen.
 */
function ResponseCopyButton({ text }: { text: string }) {
  const [copied, setCopied] = useState(false);
  const timerRef = useRef<number | undefined>(undefined);
  useEffect(() => () => window.clearTimeout(timerRef.current), []);

  const copy = async (): Promise<void> => {
    const clipboard =
      typeof navigator === "undefined" ? undefined : navigator.clipboard;
    if (!clipboard) return;
    try {
      await clipboard.writeText(text);
    } catch {
      return;
    }
    setCopied(true);
    window.clearTimeout(timerRef.current);
    timerRef.current = window.setTimeout(
      () => setCopied(false),
      COPIED_CONFIRMATION_MS,
    );
  };

  const label = copied ? "Copied" : "Copy";
  return (
    <>
      <Tooltip content={label} describeChild={false}>
        <Button
          variant="ghost"
          size="xs"
          iconOnly
          aria-label={label}
          onClick={() => void copy()}
        >
          {copied ? (
            <Check className="h-3.5 w-3.5" aria-hidden />
          ) : (
            <Copy className="h-3.5 w-3.5" aria-hidden />
          )}
        </Button>
      </Tooltip>
      <span role="status" className="sr-only">
        {copied ? "Response copied" : ""}
      </span>
    </>
  );
}
